// src/components/client/ClientFormWithSearch.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Mail, Phone, MapPin, Hash, Search, Loader2, CheckCircle, 
  AlertCircle, UserPlus, ChevronDown, Database
} from 'lucide-react';
import { Client } from '@/types/Offer';
import { useClients, ClientData } from '@/hooks/useClients';
import { ClientForm } from './ClientForm';

interface ClientFormWithSearchProps {
  client: Client;
  onChange: (client: Client) => void;
}

export const ClientFormWithSearch: React.FC<ClientFormWithSearchProps> = ({ client, onChange }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isNewClient, setIsNewClient] = useState(true);
  const searchRef = useRef<HTMLDivElement>(null);
  
  const { clients, searchClients, getClient, createClient, updateClient } = useClients();
  const [searchResults, setSearchResults] = useState<ClientData[]>([]);
  const [searching, setSearching] = useState(false);

  // Sprawdź czy klient istnieje w bazie
  useEffect(() => {
    if (client.nip && client.nip.length === 10) {
      const existingClient = clients.find(c => c.nip === client.nip);
      if (existingClient) {
        setSelectedClientId(String(existingClient.id));
        setIsNewClient(false);
      } else {
        setSelectedClientId(null);
        setIsNewClient(true);
      }
    }
  }, [client.nip, clients]);

  // Wyszukiwanie klientów
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setSearching(true);
        const results = await searchClients(searchQuery);
        setSearchResults(results);
        setSearching(false);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, searchClients]);

  // Zamknij dropdown po kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectClient = (selectedClient: ClientData) => {
    onChange({
      nip: selectedClient.nip,
      name: selectedClient.name,
      address: selectedClient.address || '',
      email: selectedClient.email || '',
      phone: selectedClient.phone || '',
      regon: selectedClient.regon || '',
      wojewodztwo: selectedClient.wojewodztwo || '',
      powiat: selectedClient.powiat || '',
      gmina: selectedClient.gmina || ''
    });
    setSelectedClientId(String(selectedClient.id));
    setIsNewClient(false);
    setShowSearch(false);
    setSearchQuery('');
  };

  const handleNewClient = () => {
    onChange({
      nip: '',
      name: '',
      address: '',
      email: '',
      phone: '',
      regon: '',
      wojewodztwo: '',
      powiat: '',
      gmina: ''
    });
    setSelectedClientId(null);
    setIsNewClient(true);
    setShowSearch(false);
    setSearchQuery('');
  };

  const handleClientChange = async (updatedClient: Client) => {
    onChange(updatedClient);
    
    // Automatycznie zapisz lub zaktualizuj klienta w bazie
    if (updatedClient.nip && updatedClient.nip.length === 10 && updatedClient.name) {
      try {
        if (isNewClient) {
          // Sprawdź czy klient z tym NIP już istnieje
          const existingClient = await getClient(updatedClient.nip);
          if (existingClient) {
            // Klient już istnieje - zaktualizuj go
            const updated = await updateClient(existingClient.id, updatedClient);
            if (updated) {
              setSelectedClientId(String(updated.id));
              setIsNewClient(false);
            }
          } else {
            // Nowy klient - utwórz go
            const newClient = await createClient(updatedClient);
            if (newClient) {
              setSelectedClientId(String(newClient.id));
              setIsNewClient(false);
            }
          }
        } else if (selectedClientId) {
          await updateClient(selectedClientId, updatedClient);
        }
      } catch (error) {
        console.error('Error saving client:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Wybór klienta */}
      <div className="relative" ref={searchRef}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-4"
        >
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-400">
              <Database className="w-4 h-4 inline mr-1" />
              Wybierz klienta z bazy lub dodaj nowego
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearch(true)}
                placeholder="Szukaj po nazwie, NIP lub email..."
                className="w-full bg-zinc-700/50 backdrop-blur rounded-xl pl-10 pr-4 py-3 text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
              />
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${showSearch ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNewClient}
            className="mt-8 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            Nowy klient
          </motion.button>
        </motion.div>

        {/* Dropdown z wynikami wyszukiwania */}
        <AnimatePresence>
          {showSearch && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 w-full bg-zinc-800 rounded-xl shadow-xl border border-zinc-700 max-h-80 overflow-y-auto"
            >
              {searching ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-500" />
                  <p className="text-sm text-gray-400 mt-2">Szukam...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="py-2">
                  {searchResults.map((result) => (
                    <motion.button
                      key={String(result.id)}
                      whileHover={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                      onClick={() => handleSelectClient(result)}
                      className="w-full px-4 py-3 text-left hover:bg-zinc-700/50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-white">{result.name}</p>
                          <p className="text-sm text-gray-400">NIP: {result.nip}</p>
                          {result.address && (
                            <p className="text-xs text-gray-500 mt-1">{result.address}</p>
                          )}
                        </div>
                        {result.id === selectedClientId && (
                          <CheckCircle className="w-5 h-5 text-emerald-500 mt-1" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              ) : searchQuery.length >= 2 ? (
                <div className="p-4 text-center text-gray-400">
                  <p>Nie znaleziono klientów</p>
                  <button
                    onClick={handleNewClient}
                    className="mt-2 text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 mx-auto"
                  >
                    <UserPlus className="w-4 h-4" />
                    Dodaj nowego klienta
                  </button>
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Wpisz minimum 2 znaki aby wyszukać
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Status klienta */}
      {(selectedClientId || isNewClient) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className={`px-4 py-3 rounded-xl flex items-center gap-2 ${
            isNewClient 
              ? 'bg-blue-900/20 border border-blue-500/30' 
              : 'bg-emerald-900/20 border border-emerald-500/30'
          }`}
        >
          {isNewClient ? (
            <>
              <UserPlus className="w-5 h-5 text-blue-400" />
              <span className="text-blue-400">Nowy klient - zostanie dodany do bazy po wypełnieniu danych</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400">Klient z bazy danych</span>
              {selectedClientId && (
                <span className="text-xs text-gray-500 ml-auto">ID: {String(selectedClientId).slice(0, 8)}...</span>
              )}
            </>
          )}
        </motion.div>
      )}

      {/* Formularz klienta */}
      <ClientForm client={client} onChange={handleClientChange} />
    </div>
  );
};
